import React, { useState } from 'react';
import { Share2, Linkedin, Twitter, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Compact share button for event cards
 * Shows a small popover with quick share options
 */
export default function QuickShareButton({ event, className = '' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const eventUrl = `https://events.jigsawconferences.co.uk/events/${event.citySlug}/${event.venueSlug}/${event.slug}`;
  
  const shareText = `Check out ${event.name} - ${event.dates?.display || ''} at ${event.venue || event.venueName || ''}, ${event.city || ''}`;

  const copyLink = async () => {
    await navigator.clipboard.writeText(eventUrl);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareToLinkedIn = () => {
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(eventUrl)}`,
      '_blank',
      'width=600,height=400'
    );
  };

  const shareToTwitter = () => {
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(eventUrl)}`,
      '_blank',
      'width=600,height=400'
    );
  };

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-full hover:bg-orange-50 text-gray-400 hover:text-orange-500 transition-colors"
        title="Share this event"
      >
        <Share2 className="w-4 h-4" />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Popover */}
          <div className="absolute right-0 top-full mt-1 z-20 bg-white rounded-lg shadow-lg border border-gray-200 p-2 min-w-[140px]">
            <button
              onClick={() => { shareToLinkedIn(); setIsOpen(false); }}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#0077B5] rounded-md transition-colors"
            >
              <Linkedin className="w-4 h-4" />
              LinkedIn
            </button>
            <button
              onClick={() => { shareToTwitter(); setIsOpen(false); }}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-black rounded-md transition-colors"
            >
              <Twitter className="w-4 h-4" />
              X / Twitter
            </button>
            <button
              onClick={() => { copyLink(); setIsOpen(false); }}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-md transition-colors"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              Copy Link
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Inline CTA banner for blogs and other pages
 */
export function BoostBanner({ variant = 'default' }) {
  if (variant === 'compact') {
    return (
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="text-2xl">🚀</span>
          <div>
            <p className="font-medium text-gray-900">Love this event?</p>
            <p className="text-sm text-gray-600">Share it and earn rewards!</p>
          </div>
        </div>
        <a 
          href="/boost/dashboard" 
          className="px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg text-sm font-medium hover:from-orange-600 hover:to-amber-600 transition-colors whitespace-nowrap"
        >
          Learn More
        </a>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 border border-amber-200 rounded-xl p-6 md:p-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">✨</span>
            <span className="text-sm font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Event Boost</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Share Events, Earn Rewards</h3>
          <p className="text-gray-600">
            Join our ambassador program! Share events with your network and earn badges, 
            climb the leaderboard, and unlock exclusive perks.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <a 
            href="/events" 
            className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-medium hover:from-orange-600 hover:to-amber-600 transition-colors text-center"
          >
            Browse Events
          </a>
          <a 
            href="/boost/dashboard" 
            className="px-6 py-3 border border-orange-300 text-orange-600 rounded-lg font-medium hover:bg-orange-50 transition-colors text-center"
          >
            My Dashboard
          </a>
        </div>
      </div>
    </div>
  );
}
